import React, { useState, useCallback } from 'react'
import EasyCropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface ImageCropModalProps {
  open: boolean
  imageUrl: string
  title?: string
  aspect?: number
  cropShape?: 'round' | 'rect'
  onClose: () => void
  onCropComplete: (croppedImage: Blob) => void
}

const OUTPUT_SIZE = 400

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', reject)
    img.setAttribute('crossOrigin', 'anonymous')
    img.src = url
  })
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = OUTPUT_SIZE
  canvas.height = OUTPUT_SIZE
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE,
  )
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas is empty'))),
      'image/webp',
      0.9,
    )
  })
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  open,
  imageUrl,
  title = 'Crop Image',
  aspect = 1,
  cropShape = 'round',
  onClose,
  onCropComplete,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const onCropCompleteCallback = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }, [])

  const handleClose = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    onClose()
  }

  const handleApply = async () => {
    if (!croppedAreaPixels) return
    const blob = await getCroppedImg(imageUrl, croppedAreaPixels)
    onCropComplete(blob)
    handleClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="relative h-72 w-full bg-gray-100 rounded-lg overflow-hidden">
          <EasyCropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            minZoom={0.95}
            aspect={aspect}
            objectFit="contain"
            cropShape={cropShape}
            showGrid={cropShape === 'rect'}
            restrictPosition={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropCompleteCallback}
          />
        </div>

        <div className="flex items-center gap-3 py-1">
          <span className="text-sm text-gray-500 w-10">Zoom</span>
          <input
            type="range"
            min={0.95}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1"
          />
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
